import React from 'react';
import { Typography } from 'antd';
import { Minus } from 'lucide-react';

import { CreativityType } from '../../../types/banner';
import { LegacyAdChannels } from '../../../types/ad-channels';

import { useClientId } from '../../../hooks/use-client-id';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { useTemplateInfo } from '../../../hooks/banners/use-template-utils';
import { useDynamicPreviews } from '../../../hooks/use-dynamic-previews';

import { RESOLUTIONS_ORDER } from '../../../constants/channels';
import { getCreativityOptions } from '../../../constants/clients/registry';

import { getClientHandler } from '../../../handlers';

import { AdChannelTabs } from './ad-channel-tabs';
import { LegacyChannelTabs } from './legacy-channel-tabs';
import { PreviewRenderer } from './preview-renderer';
import { ResolutionInfo } from './resolution-info';
import { DynamicBreadcrumbs } from './dynamic-breadcrumbs';

const { Title } = Typography;

interface DemoPreviewProps {
  creativityType: CreativityType;
  formData?: any;
}

export const DemoPreview: React.FC<DemoPreviewProps> = ({ creativityType, formData = {} }) => {
  const { spacing, fontSize, screens } = useResponsiveSizes();
  const clientId = useClientId();
  const clientHandler = getClientHandler(clientId);

  // Obtener template config
  const creativityOptions = getCreativityOptions(clientId);
  const template = creativityOptions.find(t => t.id === creativityType);

  // Sistema dinámico (custom channels)
  const {
    hasDynamicPreviews,
    previews: dynamicPreviews,
    availableChannels
  } = useDynamicPreviews(creativityType, formData, clientHandler, template);

  // Sistema legacy (videoVariants)
  const { previewVideo: defaultVideo, videoVariants, thumbnail } = useTemplateInfo(creativityType);

  // Determinar qué sistema usar
  const useDynamicSystem = hasDynamicPreviews && dynamicPreviews && Object.keys(dynamicPreviews).length > 0;
  const useLegacySystem = !useDynamicSystem && Object.keys(videoVariants).length > 0;

  // Resoluciones disponibles
  const resolutions = React.useMemo(() => {
    if (useDynamicSystem && dynamicPreviews) {
      return Object.keys(dynamicPreviews);
    }
    return RESOLUTIONS_ORDER.filter(res => Object.keys(videoVariants).includes(res));
  }, [useDynamicSystem, dynamicPreviews, videoVariants]);

  // Estados
  const [selectedCategory, setSelectedCategory] = React.useState<LegacyAdChannels>('social');
  const [selectedResolution, setSelectedResolution] = React.useState<string | null>(() => {
    return resolutions.length > 0 ? resolutions[0] : null;
  });

  // Obtener preview(s) para la resolución seleccionada
  const selectedPreviews = React.useMemo(() => {
    if (!selectedResolution) return null;

    if (useDynamicSystem && dynamicPreviews) {
      return dynamicPreviews[selectedResolution];
    }

    if (useLegacySystem) {
      return videoVariants[selectedResolution];
    }

    return null;
  }, [selectedResolution, useDynamicSystem, dynamicPreviews, useLegacySystem, videoVariants]);

  // Auto-seleccionar primera resolución disponible cuando cambian las resoluciones
  React.useEffect(() => {
    if (resolutions.length === 0) {
      setSelectedResolution(null);
      return;
    }
    if (!selectedResolution || !resolutions.includes(selectedResolution)) {
      setSelectedResolution(resolutions[0]);
    }
  }, [resolutions, selectedResolution]);

  // Función para obtener previews por canal (para AdChannelTabs)
  const getPreviewsForChannel = React.useCallback(
    (channel: string) => {
      const channelFormData = { ...formData, adChannel: channel };
      return clientHandler.getDemoPreviews?.(channelFormData, creativityType) || {};
    },
    [formData, clientHandler, creativityType]
  );

  return (
    <div
      style={{
        position: 'sticky',
        top: 16,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start'
      }}
    >
      {/* Header */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Title style={{ margin: `${spacing.sm}px 0`, fontSize: fontSize.xxl }}>Demo</Title>
      </div>

      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: spacing.xs,
          padding: screens.xs ? spacing.sm : spacing.md
        }}
      >
        {useDynamicSystem && template?.breadcrumbConfig && (
          <DynamicBreadcrumbs
            formData={formData}
            config={template.breadcrumbConfig}
            separator={<Minus width={screens.xs ? 10 : 15} />}
          />
        )}

        {/* Sistema dinámico: Tabs Google/Meta/etc */}
        {useDynamicSystem && template?.channelConfigs && availableChannels.length > 0 && (
          <AdChannelTabs
            selectedResolution={selectedResolution}
            onResolutionChange={setSelectedResolution}
            availableChannels={availableChannels}
            channelConfigs={template.channelConfigs}
            getPreviewsForChannel={getPreviewsForChannel}
          />
        )}

        {/* Sistema legacy: Tabs Social/Display/DOOH */}
        {useLegacySystem && (
          <LegacyChannelTabs
            resolutions={resolutions}
            selectedResolution={selectedResolution}
            onResolutionChange={setSelectedResolution}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        )}

        {/* Preview Renderer */}
        <PreviewRenderer previews={selectedPreviews || defaultVideo} thumbnail={thumbnail} />

        {/* Resolution Info */}
        {useLegacySystem && selectedPreviews && <ResolutionInfo resolution={selectedResolution} />}
      </div>
    </div>
  );
};
